import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'
import moment from 'moment'
import { KTSVG } from '../../../_metronic/helpers'
import { useForgotPasswordMutation } from '../../../services/authenticationApi'

type Props = {
    show: boolean
    handleClose: () => void
    data?: any
    refetch?: any
}

const PasswordResetModal = ({ show, handleClose, data, refetch }: Props) => {
    const [copied, setCopied] = useState(false)
    const [timeLeft, setTimeLeft] = useState('')
    const [isExpired, setIsExpired] = useState(false)
    const [remainingPercent, setRemainingPercent] = useState(0)

    const [forgotPasswordTrigger, { isLoading, isSuccess, isError, error }] = useForgotPasswordMutation()

    const displayName = [data?.firstName, data?.middleName, data?.lastName].filter(Boolean).join(' ') || '—'
    const userEmail = data?.email || '—'
    const ssOrgId = data?.userAccountID ?? data?.orgUserID ?? '—'
    const hasLink = !!data?.passwordResetLink

    useEffect(() => {
        if (!data?.passwordResetRequestDate) {
            setIsExpired(false)
            setTimeLeft('')
            setRemainingPercent(0)
            return
        }

        const updateTimer = () => {
            const expiryDate = moment(data.passwordResetRequestDate).add(72, 'hours')
            const now = moment()
            const totalMs = 72 * 60 * 60 * 1000
            const remainingMs = expiryDate.diff(now)

            if (remainingMs <= 0) {
                setIsExpired(true)
                setTimeLeft('Link expired')
                setRemainingPercent(0)
                return
            }

            setIsExpired(false)
            const duration = moment.duration(remainingMs)
            const days = Math.floor(duration.asDays())
            const hours = duration.hours()
            const minutes = duration.minutes()
            const seconds = duration.seconds()

            if (days > 0) {
                setTimeLeft(`${days}d ${hours}h ${minutes}m ${seconds}s`)
            } else if (hours > 0) {
                setTimeLeft(`${hours}h ${minutes}m ${seconds}s`)
            } else if (minutes > 0) {
                setTimeLeft(`${minutes}m ${seconds}s`)
            } else {
                setTimeLeft(`${seconds}s`)
            }
            setRemainingPercent(Math.max(0, Math.min(100, (remainingMs / totalMs) * 100)))
        }

        updateTimer()
        const intervalId = setInterval(updateTimer, 1000)
        return () => clearInterval(intervalId)
    }, [data?.passwordResetRequestDate])

    const handleModalClose = () => {
        setCopied(false)
        handleClose()
    }

    const handleCopyToClipboard = () => {
        if (!data?.passwordResetLink || isExpired) return
        navigator.clipboard.writeText(data.passwordResetLink)
            .then(() => {
                setCopied(true)
                toast.success('Password reset link copied to clipboard!')
                setTimeout(() => setCopied(false), 2000)
            })
            .catch(() => {
                toast.error('Failed to copy link to clipboard')
            })
    }

    const handleGenerateNewLink = () => {
        if (!data?.email) return
        forgotPasswordTrigger({ email: data.email })
            .unwrap()
            .then(() => {
                refetch?.()
                handleModalClose()
            })
            .catch(() => undefined)
    }

    useEffect(() => {
        if (isSuccess) {
            toast.success('Reset password link generated!')
        }
    }, [isSuccess])

    useEffect(() => {
        if (isError && error) {
            let errorMessage = 'Something went wrong'
            if ('data' in error && error.data && typeof error.data === 'object' && 'message' in error.data) {
                errorMessage = (error.data as { message: string }).message
            }
            toast.error(errorMessage)
        }
    }, [isError, error])

    const generateButton = (
        <button
            type='button'
            className='btn btn-primary'
            onClick={handleGenerateNewLink}
            disabled={isLoading || !data?.email}
        >
            {!isLoading && (
                <>
                    <KTSVG className='svg-icon-3 me-1' path='/media/icons/duotune/arrows/arr064.svg' />
                    Generate New Link
                </>
            )}
            {isLoading && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                    Generating...
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
            )}
        </button>
    )

    return (
        <Modal
            aria-hidden='true'
            dialogClassName='modal-dialog modal-xl modal-dialog-centered mw-900px ss-password-reset-modal ss-modal-70'
            show={show}
            onHide={handleModalClose}
        >
            <Tooltip id='reset-password-modal-actions' place='bottom' />
            <div className='modal-header'>
                <h2 className='mb-0'>Reset Password</h2>
                <div className='ss-modal-header-actions d-flex align-items-center gap-2'>
                    {generateButton}
                    <button
                        type='button'
                        className='btn btn-sm btn-icon btn-active-color-primary'
                        onClick={handleModalClose}
                        data-tooltip-id='reset-password-modal-actions'
                        data-tooltip-content='Close'
                        aria-label='Close'
                    >
                        <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
                    </button>
                </div>
            </div>

            <div className='modal-body py-6 px-8'>
                <div className='d-flex flex-column gap-2 fs-6 mb-5'>
                    <div className='d-flex align-items-baseline'>
                        <span className='fw-semibold me-2' style={{ minWidth: '6.5rem' }}>User:</span>
                        <span>{displayName}</span>
                    </div>
                    <div className='d-flex align-items-baseline'>
                        <span className='fw-semibold me-2' style={{ minWidth: '6.5rem' }}>SS Org ID:</span>
                        <span>{ssOrgId}</span>
                    </div>
                    <div className='d-flex align-items-baseline'>
                        <span className='fw-semibold me-2' style={{ minWidth: '6.5rem' }}>Email:</span>
                        <span className='text-lowercase text-break'>{userEmail}</span>
                    </div>
                </div>

                {hasLink ? (
                    <>
                        <div className='ss-reset-password-status mb-5'>
                            <div className='d-flex align-items-center justify-content-between mb-2'>
                                <span className='fw-semibold'>Link Expires In:</span>
                                <span className={`badge fs-7 px-3 py-2 ${isExpired ? 'badge-danger' : 'badge-primary'}`}>
                                    {timeLeft || '—'}
                                </span>
                            </div>
                            <div className='progress h-6px'>
                                <div
                                    className={`progress-bar ${isExpired ? 'bg-danger' : 'bg-primary'}`}
                                    role='progressbar'
                                    style={{ width: isExpired ? '100%' : `${remainingPercent}%` }}
                                />
                            </div>
                            <small className='text-muted mt-2 d-block'>
                                {isExpired ? 'This link has expired.' : 'Link is valid for 72 hours from generation.'}
                            </small>
                        </div>

                        <div className='mb-4'>
                            <label className='form-label fw-bold mb-2'>Password Reset Link</label>
                            <div className='d-flex align-items-stretch gap-2 ss-reset-password-link-row'>
                                <input
                                    type='text'
                                    className='form-control form-control-solid'
                                    value={data.passwordResetLink}
                                    readOnly
                                    placeholder='Password reset link'
                                />
                                <button
                                    type='button'
                                    className={`btn text-nowrap ${copied ? 'btn-success' : 'btn-primary'}`}
                                    onClick={handleCopyToClipboard}
                                    disabled={isExpired}
                                    data-tooltip-id='reset-password-modal-actions'
                                    data-tooltip-content={isExpired ? 'Link expired' : 'Copy reset link'}
                                    aria-label={isExpired ? 'Link expired' : 'Copy reset link'}
                                >
                                    <KTSVG
                                        className='svg-icon-2 me-1'
                                        path={copied ? '/media/icons/duotune/general/gen044.svg' : '/media/icons/duotune/files/fil005.svg'}
                                    />
                                    {copied ? 'Copied' : 'Copy'}
                                </button>
                            </div>
                            <div className='form-text mt-2'>
                                Click Copy to put this link on the clipboard.
                            </div>
                        </div>

                        {isExpired && (
                            <div className='alert alert-danger mb-0'>
                                <div className='fw-bold'>Link Expired</div>
                                <div className='fs-7'>
                                    This password reset link has expired. Generate a new one from the header.
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className='alert alert-info mb-0'>
                        <div className='fw-bold'>No password reset link</div>
                        <div className='fs-7'>
                            No password reset link is available for this user. Generate a new one from the header (valid for 72 hours).
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    )
}

export default PasswordResetModal